import { __decorate, __metadata } from 'tslib';
import { DOCUMENT, APP_BASE_HREF, PlatformLocation } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { AutoSubscription } from '@upr-web-primer/app-utils';
import * as i2 from '@upr-web-primer/coveo-search';
import { CoveoService } from '@upr-web-primer/coveo-search';
import * as i3 from '@upr-web-primer/gds-route-resolver';
import { GdsRouteResolverService } from '@upr-web-primer/gds-route-resolver';
import { WINDOW } from '@upr-web-primer/window';
import { BehaviorSubject, Subscription } from 'rxjs';

let GdsCoveoSearchService = class GdsCoveoSearchService {
    constructor(http, window, document, baseHref, coveoService, routeResolver) {
        this.http = http;
        this.window = window;
        this.document = document;
        this.baseHref = baseHref;
        this.coveoService = coveoService;
        this.routeResolver = routeResolver;
        this.coveoSearchQuery$ = new BehaviorSubject('');
        this.coveoSearchBoxes = [];
        this.subscriptions = new Subscription();
    }
    searchBarDefaultPosition() {
        this.searchBarEl?.initSearchBarPositionMethod();
    }
    open() {
        this.searchBarEl?.openSearchBar();
    }
    close() {
        this.searchBarEl?.closeSearchBar();
    }
    eventListeners() {
        this.searchBarEl?.addEventListener('searchGroupClosed', () => this.close());
        this.searchBarEl?.addEventListener('searchResultsDismissEvent', () => this.close());
        this.gdsInputEl?.addEventListener('input', (event) => this.updateInputValue(event.target?.value));
        this.gdsInputEl?.addEventListener('keydown', (event) => this.onInputKeydownEvent(event));
    }
    search(query) {
        const options = {
            headers: {
                Authorization: `Bearer ${this.coveoService.accessToken}`,
            },
        };
        const body = {
            q: query,
            locale: this.coveoService.currentLocale,
            enableWordCompletion: false,
            count: 5,
            searchHub: this.coveoService.searchHub,
        };
        return this.http.post(`https://platform.cloud.coveo.com/rest/search/v2/querySuggest`, body, options);
    }
    navigate(param, href = '') {
        const _param = param.toLowerCase();
        const anchor = href ? '' : `q=${_param}`;
        let targetUrl = this.getCoveoSearchResultsPage(href) || this.coveoSearchResultsPage;
        let target = 'self';
        if (targetUrl.startsWith(this.baseHref)) {
            targetUrl = targetUrl.replace(this.baseHref, '');
            target = '_routeLink';
        }
        this.routeResolver.resolveRoute({
            href: targetUrl,
            anchor,
            target,
        });
    }
    getCoveoSearchResultsPage(searchResultPage) {
        const origin = this.window.location.origin;
        const stringsToCheck = ['local'];
        if (searchResultPage) {
            return stringsToCheck.some((str) => origin.includes(str))
                ? searchResultPage.replace('/web', '')
                : searchResultPage;
        }
        else {
            return '';
        }
    }
    extractMatchString(url) {
        const match = decodeURIComponent(url).match(/#q=([^&]*)/);
        return match ? match[1] || '' : '';
    }
    getQueryParam() {
        return this.extractMatchString(this.window.location.hash)?.toLowerCase();
    }
    updateInputValue(value) {
        const _value = value?.toLowerCase();
        this.coveoSearchQuery$.next(_value);
        if (this.isSearchPage && this.coveoSearchBox) {
            this.coveoSearchBox.value = _value;
        }
    }
    onInputKeydownEvent(event) {
        const query = this.gdsInputEl?.value;
        if (event.key.toLowerCase() === 'enter') {
            if (this.isSearchPage) {
                this.dispatchOnSearchPage(event);
            }
            else if (query?.trim()) {
                this.navigate(query);
            }
        }
    }
    buttonClickHandler(event, btnData) {
        const { target = '', type = '', href = '', label = '', ariaLabel = '', expression = '', } = btnData;
        if (target === 'nav-search-button') {
            this.open();
        }
        else if (['nav-search', 'nav'].includes(type)) {
            const _label = expression === '' ? label : expression;
            const search = (_label || ariaLabel).toLowerCase();
            if (this.gdsInputEl) {
                this.gdsInputEl.value = search;
            }
            this.updateInputValue(search);
            this.isSearchPage && this.dispatchOnSearchPage(event);
            this.navigate(search, href);
        }
    }
    dispatchOnSearchPage(event) {
        const enterEvent = new KeyboardEvent('keydown', {
            key: event?.key ?? 'Enter',
            code: event?.code ?? 'Enter',
            which: event?.which ?? 13,
            keyCode: event?.keyCode ?? 13,
        });
        this.coveoSearchBox?.dispatchEvent(enterEvent);
    }
    updateSearchData(searchData) {
        this.searchBarEl?.setAttribute('data', JSON.stringify(searchData?.data));
        this.updateLiveRegion(searchData?.message);
    }
    updateLiveRegion(message) {
        const liveRegion = this.searchBarEl.querySelector('.gds-search-results-live-region');
        liveRegion && (liveRegion.innerHTML = message);
    }
    init(global) {
        this.searchBarEl = global?.querySelector('gds-search-bar');
        this.gdsInputEl = this.searchBarEl?.querySelector('.gds-search-input input');
        this.coveoSearchResultsPage = this.getCoveoSearchResultsPage(this.coveoService.searchResultPage);
        this.eventListeners();
        this.searchBarDefaultPosition();
        this.coveoService.isGdsGlobalNav$.next(true);
        this.subscriptions.add(this.coveoService.isSearchPage$.subscribe((val) => {
            this.isSearchPage = val;
            this.gdsInputEl && (this.gdsInputEl.value = this.getQueryParam());
            const event = new Event('input', { bubbles: true });
            this.gdsInputEl?.dispatchEvent(event);
        }));
        this.subscriptions.add(this.coveoService.isInitialized$.subscribe((val) => {
            if (val) {
                this.coveoSearchBox = this.document.querySelector('.gds-pseudo-search-box input');
            }
        }));
        this.subscriptions.add(this.coveoService.isQueryDone$.subscribe(() => {
            this.close();
        }));
    }
    ngOnDestroy() { }
};
GdsCoveoSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchService, deps: [{ token: i1.HttpClient }, { token: WINDOW }, { token: DOCUMENT }, { token: APP_BASE_HREF }, { token: i2.CoveoService }, { token: i3.GdsRouteResolverService }], target: i0.ɵɵFactoryTarget.Injectable });
GdsCoveoSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchService, providedIn: 'root' });
GdsCoveoSearchService = __decorate([
    AutoSubscription(),
    __metadata("design:paramtypes", [HttpClient, Object, Document, String, CoveoService,
        GdsRouteResolverService])
], GdsCoveoSearchService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_BASE_HREF]
                }] }, { type: i2.CoveoService }, { type: i3.GdsRouteResolverService }]; } });

class GdsCoveoSearchModule {
}
GdsCoveoSearchModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GdsCoveoSearchModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchModule });
GdsCoveoSearchModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchModule, providers: [
        {
            provide: APP_BASE_HREF,
            useFactory: (s) => s.getBaseHrefFromDOM(),
            deps: [PlatformLocation],
        },
    ], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsCoveoSearchModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                    providers: [
                        {
                            provide: APP_BASE_HREF,
                            useFactory: (s) => s.getBaseHrefFromDOM(),
                            deps: [PlatformLocation],
                        },
                    ],
                }]
        }] });

export { GdsCoveoSearchModule, GdsCoveoSearchService };
